import {
  Activity,
  AlertTriangle,
  Bot,
  Brain,
  Calculator,
  Clock,
  Code2,
  Combine,
  Cpu,
  FileText,
  GitBranch,
  Minus,
  Rss,
  Scale,
  Send,
  ShoppingCart,
  Smartphone,
  Tag,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

import type { FlowEdge, FlowNode, StepData, Tab } from "./types";

export const stepData = {
  market: {
    title: "Which market/asset class and strategy frequency do you want to trade?",
    type: "multiple",
    options: [
      {
        id: "help_select",
        title: "Help me select",
        description:
          "Not sure yet. Let the AI recommend a good starting market and duration.",
      },
      {
        id: "us_daily",
        title: "US Stocks",
        description:
          "Good data quality, standard trading hours, lower execution requirements.",
      },
      {
        id: "crypto_247",
        title: "Crypto",
        description:
          "High volatility. Trading costs and exchange risks are crucial factors.",
      },
      {
        id: "futures_intraday",
        title: "Futures",
        description:
          "Trend/arbitrage/timing. Requires strict margin and risk control.",
      },
      {
        id: "custom_market",
        title: "Type your own answer",
        description: "Specify other markets or specific requirements.",
      },
    ],
  },
  risk: {
    title: "What is your risk tolerance? (Maximum Drawdown)",
    type: "single",
    options: [
      {
        id: "risk_low",
        title: "Low Risk (< 5% Max Drawdown)",
        description:
          "Focus on capital preservation, steady but lower returns.",
      },
      {
        id: "risk_med",
        title: "Medium Risk (5% - 15% Max Drawdown)",
        description: "Balanced approach, moderate volatility.",
      },
      {
        id: "risk_high",
        title: "High Risk (> 15% Max Drawdown)",
        description: "Aggressive growth, comfortable with significant swings.",
      },
    ],
  },
  return: {
    title: "What are your return targets? (Annualized)",
    type: "single",
    options: [
      {
        id: "ret_stable",
        title: "Stable Income (5% - 10%)",
        description:
          "Consistent returns, similar to fixed income or standard index growth.",
      },
      {
        id: "ret_market",
        title: "Market Outperformance (10% - 20%)",
        description:
          "Beat the benchmark index (e.g., S&P 500) consistently.",
      },
      {
        id: "ret_high",
        title: "High Alpha (> 20%)",
        description: "Seek significant excess returns, usually paired with higher risk.",
      },
    ],
  },
  interpret: {
    title: "Do you require the strategy to be easily interpretable?",
    type: "single",
    options: [
      {
        id: "interp_white",
        title: "White Box (Highly Interpretable)",
        description:
          "Rules-based, linear regression. Easy to understand why a trade happens.",
      },
      {
        id: "interp_gray",
        title: "Gray Box (Somewhat Interpretable)",
        description:
          "Machine learning (e.g., Random Forests) where feature importance can be extracted.",
      },
      {
        id: "interp_black",
        title: "Black Box (Low Interpretability)",
        description:
          "Deep learning, neural networks. Focuses purely on predictive power.",
      },
    ],
  },
  constraints: {
    title: "Are there any specific constraints we should know about?",
    type: "multiple",
    options: [
      {
        id: "const_capital",
        title: "Limited Capital",
        description: "Cannot trade high-priced stocks or high-margin futures.",
      },
      {
        id: "const_leverage",
        title: "No Leverage / Margin",
        description:
          "Strategy must be strictly cash-only without borrowing.",
      },
      {
        id: "const_tech",
        title: "Low Execution Speed",
        description:
          "Cannot compete in HFT or sub-second latency environments.",
      },
      {
        id: "custom_const",
        title: "Other Constraints",
        description: "Type your own constraint.",
      },
    ],
  },
} satisfies StepData;

export const tabsList = [
  { id: "market", label: "Market" },
  { id: "risk", label: "Risk" },
  { id: "return", label: "Return" },
  { id: "interpret", label: "Interpretability" },
  { id: "constraints", label: "Constraints" },
] satisfies Tab[];

export const flowNodes = [
  {
    id: "rss-1",
    label: "RSS Feed",
    sub: "News Source",
    x: 50,
    y: -140,
    step: 4,
    icon: Rss,
    color: "text-slate-700",
    borderColor: "border-slate-200",
    bg: "bg-slate-50",
  },
  {
    id: "rss-2",
    label: "RSS Feed",
    sub: "News Source",
    x: 50,
    y: 0,
    step: 4,
    icon: Rss,
    color: "text-slate-700",
    borderColor: "border-slate-200",
    bg: "bg-slate-50",
  },
  {
    id: "rss-3",
    label: "RSS Feed",
    sub: "News Source",
    x: 50,
    y: 140,
    step: 4,
    icon: Rss,
    color: "text-slate-700",
    borderColor: "border-slate-200",
    bg: "bg-slate-50",
  },
  {
    id: "sentiment",
    label: "Sentiment Factor",
    sub: "Text Analysis",
    x: 280,
    y: 20,
    step: 4,
    icon: Brain,
    color: "text-amber-600",
    borderColor: "border-amber-200",
    bg: "bg-amber-50",
  },
  {
    id: "kline",
    label: "K-Line Trigger",
    sub: "Close Price",
    x: 50,
    y: 250,
    step: 1,
    icon: Activity,
    color: "text-green-500",
    borderColor: "border-green-200",
    bg: "bg-green-50",
  },
  {
    id: "sema",
    label: "Short EMA",
    sub: "Indicator",
    x: 280,
    y: 150,
    step: 1,
    icon: TrendingUp,
    color: "text-teal-500",
    borderColor: "border-teal-200",
    bg: "bg-teal-50",
  },
  {
    id: "lema",
    label: "Long EMA",
    sub: "Indicator",
    x: 280,
    y: 350,
    step: 1,
    icon: TrendingUp,
    color: "text-teal-500",
    borderColor: "border-teal-200",
    bg: "bg-teal-50",
  },
  {
    id: "append",
    label: "Append",
    sub: "Indicators",
    x: 510,
    y: 250,
    step: 1,
    icon: Combine,
    color: "text-blue-500",
    borderColor: "border-blue-200",
    bg: "bg-blue-50",
  },
  {
    id: "calc",
    label: "Calculate",
    sub: "Logic",
    x: 740,
    y: 250,
    step: 2,
    icon: Calculator,
    color: "text-orange-500",
    borderColor: "border-orange-200",
    bg: "bg-orange-50",
  },
  {
    id: "branch",
    label: "Branch",
    sub: "Rules",
    x: 970,
    y: 250,
    step: 3,
    icon: GitBranch,
    color: "text-indigo-500",
    borderColor: "border-indigo-200",
    bg: "bg-indigo-50",
  },
  {
    id: "buy",
    label: "Buy",
    sub: "Action",
    x: 1200,
    y: 100,
    step: 3,
    icon: ShoppingCart,
    color: "text-red-500",
    borderColor: "border-red-200",
    bg: "bg-red-50",
  },
  {
    id: "sell",
    label: "Sell",
    sub: "Action",
    x: 1200,
    y: 250,
    step: 3,
    icon: Tag,
    color: "text-emerald-500",
    borderColor: "border-emerald-200",
    bg: "bg-emerald-50",
  },
  {
    id: "log",
    label: "Log",
    sub: "System",
    x: 1200,
    y: 400,
    step: 3,
    icon: FileText,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
] satisfies FlowNode[];

// AI Trading Strategy nodes
// Stage mapping:
//   step 1 (baseline/1): timer, sentfetch, kline2
//   step 2 (2): + accinfo, indicator2
//   step 3 (3): + merge, normalize, tradedecision, telegram, longopen/shortopen/flatlong/flatshort/stoploss + pushes
//   step 4 (enhanced): + aisentiment, openai1, aitrading, openai2, openai3
// Coordinates updated from ai_2.json / ai_3.json / ai_enhanced.json
export const aiTradingFlowNodes = [
  // ── Step 1: Timer trigger + two data collectors ──────────────────────────
  {
    id: "timer",
    label: "Timer Trigger",
    sub: "Scheduled",
    x: 60,
    y: 340,
    step: 1,
    icon: Clock,
    color: "text-green-600",
    borderColor: "border-green-300",
    bg: "bg-green-50",
  },
  {
    id: "sentfetch",
    label: "Fetch Sentiment",
    sub: "Sentiment Score",
    x: 240,
    y: 220,
    step: 1,
    icon: Brain,
    color: "text-slate-600",
    borderColor: "border-slate-200",
    bg: "bg-slate-50",
  },
  {
    id: "kline2",
    label: "Fetch K-Line",
    sub: "K-Line Data",
    x: 240,
    y: 460,
    step: 1,
    icon: Activity,
    color: "text-indigo-500",
    borderColor: "border-indigo-200",
    bg: "bg-indigo-50",
  },

  // ── Step 2: Account info + indicator (ai_2 layout) ───────────────────────
  {
    id: "accinfo",
    label: "Account Info",
    sub: "Query Holdings",
    x: 442,
    y: 218,
    step: 2,
    icon: FileText,
    color: "text-purple-600",
    borderColor: "border-purple-200",
    bg: "bg-purple-50",
  },
  {
    id: "indicator2",
    label: "Indicator Calc",
    sub: "Stock Indicators",
    x: 440,
    y: 460,
    step: 2,
    icon: Calculator,
    color: "text-blue-500",
    borderColor: "border-blue-200",
    bg: "bg-blue-50",
  },

  // ── Step 3: Merge → Normalize → Trade Decision + fan-out (ai_3 layout) ───
  {
    id: "merge",
    label: "Data Merge",
    sub: "Append",
    x: 671,
    y: 304,
    step: 3,
    icon: Combine,
    color: "text-orange-500",
    borderColor: "border-orange-200",
    bg: "bg-orange-50",
  },
  {
    id: "normalize",
    label: "Normalize",
    sub: "Standardize",
    x: 864,
    y: 305,
    step: 3,
    icon: Code2,
    color: "text-amber-600",
    borderColor: "border-amber-200",
    bg: "bg-amber-50",
  },
  {
    id: "tradedecision",
    label: "Trade Decision",
    sub: "Decision Engine",
    x: 1073,
    y: 306,
    step: 3,
    icon: Scale,
    color: "text-indigo-600",
    borderColor: "border-indigo-200",
    bg: "bg-indigo-50",
  },
  {
    id: "telegram",
    label: "Telegram Push",
    sub: "sendMessage",
    x: 1181,
    y: 73,
    step: 3,
    icon: Send,
    color: "text-blue-500",
    borderColor: "border-blue-200",
    bg: "bg-blue-50",
  },
  {
    id: "longopen",
    label: "Long Open",
    sub: "Buy Action",
    x: 1361,
    y: 31,
    step: 3,
    icon: TrendingUp,
    color: "text-red-500",
    borderColor: "border-red-200",
    bg: "bg-red-50",
  },
  {
    id: "longopen_push",
    label: "Long Push",
    sub: "Push Notify",
    x: 1523,
    y: 30,
    step: 3,
    icon: Smartphone,
    color: "text-gray-600",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "shortopen",
    label: "Short Open",
    sub: "Sell Action",
    x: 1362,
    y: 151,
    step: 3,
    icon: TrendingDown,
    color: "text-emerald-500",
    borderColor: "border-emerald-200",
    bg: "bg-emerald-50",
  },
  {
    id: "shortopen_push",
    label: "Short Push",
    sub: "Push Notify",
    x: 1520,
    y: 150,
    step: 3,
    icon: Smartphone,
    color: "text-gray-600",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "flatlong",
    label: "Flat Long",
    sub: "Close Position",
    x: 1359,
    y: 280,
    step: 3,
    icon: Minus,
    color: "text-gray-600",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "flatlong_push",
    label: "Flat Long Push",
    sub: "Push Notify",
    x: 1520,
    y: 280,
    step: 3,
    icon: Smartphone,
    color: "text-gray-600",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "flatshort",
    label: "Flat Short",
    sub: "Close Position",
    x: 1359,
    y: 404,
    step: 3,
    icon: Minus,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "flatshort_push",
    label: "Flat Short Push",
    sub: "Push Notify",
    x: 1522,
    y: 402,
    step: 3,
    icon: Smartphone,
    color: "text-gray-600",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "stoploss",
    label: "Stop Loss",
    sub: "Risk Management",
    x: 1360,
    y: 547,
    step: 3,
    icon: AlertTriangle,
    color: "text-blue-600",
    borderColor: "border-blue-200",
    bg: "bg-blue-50",
  },
  {
    id: "noaction_push",
    label: "No Action Push",
    sub: "Push Notify",
    x: 1513,
    y: 547,
    step: 3,
    icon: Smartphone,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },

  // ── Step 4: AI sentiment chain + full pipeline (ai_enhanced layout) ──────
  // accinfo moves to x=534,y=141; indicator2 moves to x=541,y=461
  // merge/normalize/tradedecision/telegram/actions shift right to make room
  // New nodes: aisentiment, openai1, aitrading, openai2, openai3
  {
    id: "aisentiment",
    label: "AI Sentiment",
    sub: "Chat Model",
    x: 439,
    y: 298,
    step: 4,
    icon: Bot,
    color: "text-blue-600",
    borderColor: "border-blue-300",
    bg: "bg-blue-50",
  },
  {
    id: "openai1",
    label: "OpenAI Model",
    sub: "LLM",
    x: 617,
    y: 298,
    step: 4,
    icon: Cpu,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "aitrading",
    label: "AI Trading",
    sub: "Chat Model",
    x: 1280,
    y: 300,
    step: 4,
    icon: Bot,
    color: "text-blue-600",
    borderColor: "border-blue-300",
    bg: "bg-blue-50",
  },
  {
    id: "openai2",
    label: "OpenAI Model 1",
    sub: "LLM",
    x: 1373,
    y: 411,
    step: 4,
    icon: Cpu,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
  {
    id: "openai3",
    label: "OpenAI Model 2",
    sub: "LLM",
    x: 1682,
    y: 300,
    step: 4,
    icon: Cpu,
    color: "text-gray-500",
    borderColor: "border-gray-200",
    bg: "bg-gray-50",
  },
] satisfies FlowNode[];

// Enhanced node positions for step 4 (ai_enhanced layout).
// When step === 4, accinfo and indicator2 appear at new coordinates
// alongside the new AI nodes; they are handled by the FlowCanvas
// via the step-4 override map below.
export const aiTradingStep4Overrides: Record<string, { x: number; y: number }> = {
  accinfo:    { x: 534, y: 141 },
  indicator2: { x: 541, y: 461 },
  merge:      { x: 880, y: 300 },
  normalize:  { x: 1080, y: 300 },
  tradedecision: { x: 1486, y: 301 },
  telegram:   { x: 1790, y: 52 },
  longopen:   { x: 1978, y: 62 },
  longopen_push: { x: 2168, y: 61 },
  shortopen:  { x: 1976, y: 184 },
  shortopen_push: { x: 2169, y: 182 },
  flatlong:   { x: 1976, y: 300 },
  flatlong_push: { x: 2165, y: 299 },
  flatshort:  { x: 1977, y: 421 },
  flatshort_push: { x: 2167, y: 422 },
  stoploss:   { x: 1976, y: 545 },
  noaction_push: { x: 2167, y: 547 },
};

export const aiTradingFlowEdges = [
  // Step 1: timer → sentfetch + kline2
  { id: "ai-e1", from: "timer", to: "sentfetch", step: 1, label: "" },
  { id: "ai-e2", from: "timer", to: "kline2",    step: 1, label: "" },

  // Step 2: sentfetch → accinfo; kline2 → indicator2
  { id: "ai-e3", from: "sentfetch",  to: "accinfo",    step: 2, label: "" },
  { id: "ai-e5", from: "kline2",     to: "indicator2", step: 2, label: "K-Line" },

  // Step 3: accinfo + indicator2 → merge → normalize → tradedecision → fan-out
  { id: "ai-e7",  from: "accinfo",      to: "merge",         step: 3, label: "Input 1" },
  { id: "ai-e9",  from: "indicator2",   to: "merge",         step: 3, label: "Input 3" },
  { id: "ai-e10", from: "merge",        to: "normalize",     step: 3, label: "" },
  // normalize→tradedecision only in step 3; step 4 routes via aitrading instead
  { id: "ai-e10b", from: "normalize",      to: "tradedecision", step: 3, label: "",        maxStep: 4 },
  // tradedecision fan-out only in step 3; step 4 routes via openai3 instead
  { id: "ai-e15b", from: "tradedecision",  to: "telegram",      step: 3, label: "",        maxStep: 4 },
  { id: "ai-e16b", from: "tradedecision",  to: "longopen",      step: 3, label: "",        maxStep: 4 },
  { id: "ai-e17",  from: "longopen",       to: "longopen_push",  step: 3, label: "" },
  { id: "ai-e18b", from: "tradedecision",  to: "shortopen",     step: 3, label: "",        maxStep: 4 },
  { id: "ai-e19",  from: "shortopen",      to: "shortopen_push", step: 3, label: "" },
  { id: "ai-e20b", from: "tradedecision",  to: "flatlong",      step: 3, label: "",        maxStep: 4 },
  { id: "ai-e21",  from: "flatlong",       to: "flatlong_push",  step: 3, label: "" },
  { id: "ai-e22b", from: "tradedecision",  to: "flatshort",     step: 3, label: "",        maxStep: 4 },
  { id: "ai-e23",  from: "flatshort",      to: "flatshort_push", step: 3, label: "" },
  { id: "ai-e24b", from: "tradedecision",  to: "stoploss",      step: 3, label: "",        maxStep: 4 },
  { id: "ai-e25",  from: "stoploss",       to: "noaction_push",  step: 3, label: "" },

  // Step 4: add aisentiment chain + aitrading + openai nodes
  { id: "ai-e4",  from: "sentfetch",   to: "aisentiment", step: 4, label: "Score" },
  { id: "ai-e6",  from: "aisentiment", to: "openai1",     step: 4, label: "Model" },
  { id: "ai-e8",  from: "openai1",     to: "merge",       step: 4, label: "Input 2" },
  { id: "ai-e11", from: "normalize",   to: "aitrading",   step: 4, label: "" },
  { id: "ai-e12", from: "aitrading",   to: "openai2",     step: 4, label: "Model" },
  { id: "ai-e13", from: "aitrading",   to: "tradedecision", step: 4, label: "" },
  { id: "ai-e14", from: "tradedecision", to: "openai3",   step: 4, label: "Model" },
  { id: "ai-e15", from: "openai3",     to: "telegram",    step: 4, label: "" },
  { id: "ai-e16", from: "openai3",     to: "longopen",    step: 4, label: "Long" },
  { id: "ai-e18", from: "openai3",     to: "shortopen",   step: 4, label: "Short" },
  { id: "ai-e20", from: "openai3",     to: "flatlong",    step: 4, label: "Flat" },
  { id: "ai-e22", from: "openai3",     to: "flatshort",   step: 4, label: "Flat" },
  { id: "ai-e24", from: "openai3",     to: "stoploss",    step: 4, label: "StopLoss" },
] satisfies FlowEdge[];

export const flowEdges = [
  { id: "e-rss-1", from: "rss-1", to: "sentiment", step: 4, label: "" },
  { id: "e-rss-2", from: "rss-2", to: "sentiment", step: 4, label: "" },
  { id: "e-rss-3", from: "rss-3", to: "sentiment", step: 4, label: "" },
  { id: "e-sentiment-append", from: "sentiment", to: "append", step: 4, label: "" },
  { id: "e1", from: "kline", to: "sema", step: 1, label: "1 item" },
  { id: "e2", from: "kline", to: "lema", step: 1, label: "1 item" },
  { id: "e3", from: "sema", to: "append", step: 1, label: "Input 1" },
  { id: "e4", from: "lema", to: "append", step: 1, label: "Input 2" },
  { id: "e5", from: "append", to: "calc", step: 2, label: "2 items" },
  { id: "e6", from: "calc", to: "branch", step: 3, label: "1 item" },
  { id: "e7", from: "branch", to: "buy", step: 3, label: "True" },
  { id: "e8", from: "branch", to: "sell", step: 3, label: "False" },
  { id: "e9", from: "branch", to: "log", step: 3, label: "Log" },
] satisfies FlowEdge[];
